import { faPenToSquare } from "@fortawesome/pro-duotone-svg-icons";
import { faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faAngleRight, faMagnifyingGlass, faPlus } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { AutoComplete, Button, Card, message, Popconfirm, Select, Skeleton, Spin, Table, Tag } from "antd";
import { Option } from "antd/es/mentions";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ImportDrawer from "../components/ImportDrawer";
import { useAuth } from "../config/AuthProvider";
import { useRoles } from "../customHooks/useRoles";
import { fromUtc } from "../helpers/dayjsSetup";
import UserService from "../services/User/UserService";
import AppButton from "../common/AppButton";

function UserPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  // const [open, setOpen] = useState(false);
  // const [type, setType] = useState("");
  const [dataSource, setDataSource] = useState([]);
  // const [editingUser, setEditingUser] = useState(null);
  const [openImport, setOpenImport] = useState(false);
  // const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { data: roles } = useRoles();
  const [list, setList] = useState({
    request: {
      id: location?.state?.selectedUserId || undefined,
      search: "",
      firstName: "",
      lastName: "",
      email: "",
      orgID: "",
      role: "",
      timezone: "",
      isActive: false,
      pageFilter: { all: false, size: 50, num: 0 },
      sortFilter: { field: "", order: "" },
    },
    response: { total: 0, data: [] },
  });
  const [query, setQuery] = useState(location?.state?.query || "");
  const [results, setResults] = useState(location?.state?.results || []);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState(
    location?.state?.selectedUserId || null
  );

  useEffect(() => {
    fetchUsers(list.request);
  }, [list.request]);

  const handleEdit = (record) => {
    navigate("/settings/user/details", {
      state: {
        roles,
        userRole: user.role,
        query,
        results,
        selectedUserId,
        pathname: "/settings/user-roles",
        initialValues: record,
        pageMode: "edit",
      },
    });
  };

  // const exportToExcel = (request) => {
  //   UserService.exportToExcel(request)
  //     .then((response) => {
  //       const url = window.URL.createObjectURL(new Blob([response.data]));
  //       const link = document.createElement("a");
  //       link.href = url;
  //       const contentDisposition = response.headers["content-disposition"];
  //       const filename =
  //         contentDisposition?.match(/filename="(.+)"/)?.[1] || "Users.xlsx";
  //       link.setAttribute("download", filename);
  //       document.body.appendChild(link);
  //       link.click();
  //       document.body.removeChild(link);
  //       message.success("Excel file downloaded successfully.");
  //     })
  //     .catch((error) => {
  //       console.error("Error exporting to Excel:", error);
  //       message.error("Failed to download Excel file.");
  //     });
  // };

  const handleTableChange = (pagination, filters, sorter) => {
    const updatedRequest = {
      ...list.request,
      sortFilter: {
        field: sorter.field || "",
        order:
          sorter.order === "ascend"
            ? "asc"
            : sorter.order === "descend"
              ? "desc"
              : "",
      },
      pageFilter: {
        ...list.request.pageFilter,
        size: pagination.pageSize,
        num: pagination.current - 1,
      },
    };

    setList((prevState) => ({
      ...prevState,
      request: updatedRequest,
    }));
  };

  // const getColumnData = (data) => {
  //   return allRoles
  //     ?.filter((ar) => data?.some((element) => element === ar.id))
  //     ?.map((ar) => ar.name);
  // };

  const handleStatusChange = async (id) => {
    try {
      const record = dataSource.data.find((item) => item.id === id);
      if (record) {
        const request = { ...record, isActive: !record.isActive };
        const response = await UserService.updateUser(request);
        if (response?.status === 200) {
          message.success(`The user has been ${request.isActive ? "activated" : "detached/deactivated"} successfully`);
          fetchUsers(list.request);
        } else {
          message.error("Something went wrong");
        }
      }
    }
    catch (error) {
      message.error(error?.response?.data?.message || "Something went wrong");
    }
  };

  // const handleApplyFilters = (values) => {
  //   setList((prevState) => ({
  //     ...prevState,
  //     request: values,
  //   }));
  // };

  const fetchUsers = (request) => {
    setIsLoading(true);
    UserService.fetchAllUsers(request)
      .then((response) => {
        if (response.status === 200) {
          setDataSource(response.data);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        console.error("Error fetching users:", error);
      });
  };

  const renderPagination = () => {
    const current = (list.request?.pageFilter?.num ?? 0) + 1;
    const pageSize = list.request?.pageFilter?.size ?? 20;
    const total = dataSource?.total ?? 0;
    const totalPages = Math.ceil(total / pageSize);

    const currentSorter = {
      field: list.request.sortFilter.field,
      order:
        list.request.sortFilter.order === "asc"
          ? "ascend"
          : list.request.sortFilter.order === "desc"
            ? "descend"
            : "",
    };

    const onCustomPageChange = (page, size) => {
      handleTableChange({ current: page, pageSize: size }, {}, currentSorter);
    };

    const onPageSizeChange = (size) => {
      onCustomPageChange(1, size); // Reset to first page when size changes
    };

    return (
      <div className="flex justify-between items-center mt-2 px-2">
        {/* Rows per page selector + info */}
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Rows per page:</span>
          <Select
            value={String(pageSize || 20)}
            onChange={(val) => onPageSizeChange(Number(val))}
            size="medium"
            style={{ width: 80 }}
          >
            <Option value="10">10</Option>
            <Option value="20">20</Option>
            <Option value="50">50</Option>
            <Option value="100">100</Option>
          </Select>
        </div>

        {/* Custom buttons */}
        <div className="flex items-center gap-4">
          <span className="text-sm text-gray-600">
            {`${(current - 1) * pageSize + 1}-${Math.min(
              current * pageSize,
              total
            )} of ${total} items`}
          </span>
          <div className="flex gap-2">
            <Button
              size="medium"
              onClick={() => onCustomPageChange(1, pageSize)}
              disabled={current === 1}
            >
              <FontAwesomeIcon icon={faAngleDoubleLeft} />
            </Button>
            <Button
              size="medium"
              onClick={() => onCustomPageChange(current - 1, pageSize)}
              disabled={current === 1}
            >
              <FontAwesomeIcon icon={faAngleLeft} />
            </Button>
            <Button
              size="medium"
              onClick={() => onCustomPageChange(current + 1, pageSize)}
              disabled={current === totalPages}
            >
              <FontAwesomeIcon icon={faAngleRight} />
            </Button>
            <Button
              size="medium"
              onClick={() => onCustomPageChange(totalPages, pageSize)}
              disabled={current === totalPages}
            >
              <FontAwesomeIcon icon={faAngleDoubleRight} />
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: "",
      key: "action",
      align: "left",
      render: (text, record) => {
        if (isLoading) {
          return <Skeleton active size="small" />;
        }
        return (
          // <div className="flex justify-center space-x-4">
          //   <Tooltip title="Edit">
          //     <Button onClick={() => handleEdit(record)} type="link" className="text-black">
          //       <EditOutlined />
          //     </Button>
          //   </Tooltip>
          // </div>
          <Button
            type="link"
            onClick={() =>
              handleEdit(record)
            }
          >
            <FontAwesomeIcon
              icon={faPenToSquare}
              className="text-xl text-black p-2 hover:text-cyan-500"
            />
          </Button>
        );
      },
    },
    {
      title: "First Name",
      dataIndex: "firstName",
      key: "firstName",
      align: "left",
      sorter: true,
      width: 150,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "Last Name",
      dataIndex: "lastName",
      key: "lastName",
      align: "left",
      width: 150,
      sorter: true,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      align: "left",
      sorter: true,
      render: (text) => (isLoading ? <Skeleton active size="small" /> : text),
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      align: "left",
      sorter: true,
      render: (text) =>
        isLoading ? (
          <Skeleton active size="small" />
        ) : text ? (
          fromUtc(text).format("DD MMM YYYY")
        ) : (
          "NA"
        ),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
      align: "center",
      sorter: false,
      render: (role) => {
        const roleName = roles?.find((ar) => ar.id === role)?.name
        return roleName ? (
          <Tag
            className="text-center bg-[#01A4A6] bg-opacity-20 rounded-xl border border-[#01A4A6] text-[#007577]"
            key={roleName}
          >
            {roleName}
          </Tag>
        ) : (
          <Skeleton active size="small" />
        );
      },
    },
    // {
    //   title: "Sign Up",
    //   dataIndex: "signUp",
    //   key: "signUp",
    //   align: "center",
    //   sorter: false,
    //   render: (text) =>
    //     isLoading ? (
    //       <Skeleton active size="small" />
    //     ) : text ? (
    //       fromUtc(text).format("DD MMM YYYY")
    //     ) : (
    //       "NA"
    //     ),
    // },
    {
      title: "Active",
      dataIndex: "isActive",
      key: "isActive",
      sorter: false,
      align: "center",
      width: 150,
      render: (status, record) => {
        if (isLoading) {
          return <Skeleton active size="small" />;
        }
        return (
          <Popconfirm
            title={`Are you sure to ${status ? "detach/deactivate" : "activate"} this user?`}
            onConfirm={() => handleStatusChange(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <div className="flex items-center justify-center">
              <span
                className={`w-4 h-4 opacity-60 rounded-full ${status ? "bg-green-500" : "bg-red-500"
                  } hover:cursor-pointer`}
              />
            </div>
          </Popconfirm>
        );
      },
    },
    {
      title: "Last Login",
      dataIndex: "lastLoginAt",
      key: "lastLoginAt",
      width: 150,
      sorter: true,
      align: "left",
      render: (text) =>
        isLoading ? (
          <Skeleton active size="small" />
        ) : text ? (
          fromUtc(text).format("DD MMM YYYY HH:mm")
        ) : (
          "NA"
        ),
    }

  ];

  const doSearch = async (term) => {
    if (!term.trim()) {
      setResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await UserService.fetchAllUsers({ search: term });
      setResults(response.data?.data || []);
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const delay = setTimeout(() => doSearch(query), 400);
    return () => clearTimeout(delay);
  }, [query]);

  return (
    <div className="px-8 mb-4">
      <div className="flex justify-between items-end w-full my-2 px-2">
        <span
          className="!font-medium text-[30px] !text-[#015373] !m-0 pl-4 mt-10 font-inter"
        >
          User
        </span>

        <div className="flex justify-end items-center w-1/2 gap-2 mr-8">
          <AutoComplete
            className="border w-72 font-inter !bg-gray-300"
            placeholder="Search users"
            prefix={<FontAwesomeIcon icon={faMagnifyingGlass} className="opacity-70 mr-2" />}
            value={query}
            onChange={setQuery}
            options={results.map(u => ({
              value: u.id,
              label: `${u.firstName} ${u.lastName}`,
              user: u
            }))}
            onSelect={(id, option) => {
              setQuery(option.label);
              setResults([]);
              doSearch("");
              fetchUsers({ ...list.request, id });
              setSelectedUserId(id);
            }}
            allowClear
            onClear={() => {
              setQuery("");
              setResults([]);
              setList(prev => ({
                ...prev,
                request: {
                  ...prev.request,
                  id: undefined
                }
              }));
              setSelectedUserId(null);
              fetchUsers(list.request);
            }}
            notFoundContent={searchLoading ? <Spin size="small" /> : "No results found"}
          />
          {["ContiAdmin", "OrgAdmin"].includes(user.role) &&
            <AppButton variant="new" onClick={() =>
                navigate("/settings/user/details", {
                  state: {
                    roles,
                    userRole: user.role,
                    query,
                    results,
                    selectedUserId,
                    pathname: "/settings/user-roles",
                    mode: "add",
                  },
                })
              }>
                New
              </AppButton>
          }

          {/* <Button
            className="font-bold rounded-lg shadow-md py-4 px-2 bg-[#4C8BCA] text-white hover:bg-[#356492]"
            type="secondary"
            onClick={() => setOpenImport(true)}
          >
            <FontAwesomeIcon icon={faArrowDownToBracket} />
            Import
          </Button>
          <Button
            className="font-bold rounded-lg shadow-md py-4 px-2  bg-[#FFA242] hover:bg-[#e9923c] text-white"
            type="secondary"
            onClick={() => exportToExcel(list.request)}
          >
            <FontAwesomeIcon icon={faArrowUpFromBracket} />
            Export
          </Button> */}
        </div>
      </div>
      <Card className="rounded-3xl shadow-xl bg-white bg-opacity-80">
        <Table
          className="mx-0 mt-4 border-2 border-gray-300 rounded-lg overflow-x-auto"
          // rowSelection={getRowSelection()}
          columns={columns}
          dataSource={
            isLoading
              ? new Array(10).fill(null).map((_, index) => ({ key: index }))
              : dataSource?.data?.map((item, index) => ({
                ...item,
                key: index,
              }))
          }
          pagination={false}
          onChange={handleTableChange}
          bordered={false}
          scroll={{ x: "max-content", y: "calc(100vh - 270px)" }}
          size="middle"
          rowClassName={(_, index) =>
            index % 2 === 0 ? "text-sm" : "text-sm bg-[#f3f3f3]"
          }
        />
        {renderPagination()}
      </Card>
      <ImportDrawer
        open={openImport}
        onClose={() => setOpenImport(false)}
        type={"UserImport"}
      />
    </div>
  );
}

export default UserPage;
